import { pickLocale, type CheckboxCharacteristic, type RadioButtonsCharacteristic } from '../../schema';
import { useCharacteristicSelection, useStoreActions } from '../../state/StoreContext';
import { useRuleResults } from '../../rules/RuleResultsContext';
import { useEffectiveValuePrice, useValuePriceFormatter } from '../../state/ComponentContext';
import { useTranslation } from '../../i18n';
import { HtmlContent } from '../HtmlContent';
import { QtyInput } from './QtyInput';

interface ToggleViewProps {
    characteristic: RadioButtonsCharacteristic | CheckboxCharacteristic;
    locale: string | undefined;
}

/**
 * Interactive radio (single-pick) or checkbox (multi-pick) group.
 */
export function ToggleView({ characteristic, locale }: ToggleViewProps) {
    const { t } = useTranslation();
    const isRadio = characteristic.type === 'radiobuttons';
    const name = `cfg-${characteristic.id}`;
    const selection = useCharacteristicSelection(characteristic.id);
    const { selectValue, toggleValue, setPickQty } = useStoreActions();
    const { blacklistedValues } = useRuleResults();
    // Several class names here are a documented theming contract, see
    // tests/stableClasses.test.tsx before renaming any.
    const formatValuePrice = useValuePriceFormatter();
    const effectivePrice = useEffectiveValuePrice();

    const singleId = selection?.kind === 'single' ? selection.valueId : null;
    const multiIds = selection?.kind === 'multi' ? selection.valueIds : [];

    // Blacklisted Values are hidden entirely (default storefront behaviour).
    // A future Component-level "show as disabled" admin setting will flip
    // this — gated on frontendVersion so it stays invisible for legacy.
    const visibleValues = characteristic.values.filter(
        (v) => !blacklistedValues.has(v.id),
    );

    if (visibleValues.length === 0) {
        return <p className="cfg-group__empty">{t('characteristic.no_values')}</p>;
    }

    // Relative-price base: the currently picked Value's effective price
    // (radio only). Multi-pick checkboxes have no single base — formatter
    // ignores the arg in absolute mode anyway.
    const currentPickValue = isRadio
        ? characteristic.values.find((v) => v.id === singleId)
        : undefined;
    const currentPickPrice = currentPickValue
        ? effectivePrice(currentPickValue, characteristic)
        : undefined;

    const showValueImages = characteristic.valueImageVisibility !== 'hide';

    const qtyMode = characteristic.quantityMode;
    const qtys = selection?.kind === 'multi' ? selection.qtys : undefined;
    const charLabel = pickLocale(characteristic.title, locale) ?? '';

    const list = (
        <ul className="cfg-toggle-list" role={isRadio ? 'radiogroup' : 'group'}>
            {visibleValues.map((value) => {
                const label = pickLocale(value.title, locale) ?? value.value ?? `#${value.id}`;
                const info = pickLocale(value.info, locale);
                const additionalInfo = pickLocale(value.additionalInfo, locale);
                const priceSuffix = formatValuePrice(effectivePrice(value, characteristic), currentPickPrice);
                const checked = isRadio
                    ? singleId === value.id
                    : multiIds.includes(value.id);
                const imgSrc = value.thumbnail ?? value.image;
                const renderImage =
                    showValueImages &&
                    imgSrc &&
                    (characteristic.valueImageVisibility === 'always' ||
                        (characteristic.valueImageVisibility === 'selected' && checked));
                return (
                    <li
                        key={value.id}
                        className="cfg-toggle-item"
                        data-configento-value-id={value.id}
                    >
                        {/* The whole row is a label — clicks on text + price
                            also flip the input, so users don't have to aim at
                            the dot/box itself. */}
                        <label className="cfg-toggle-item__label">
                            <input
                                type={isRadio ? 'radio' : 'checkbox'}
                                name={name}
                                value={value.id}
                                checked={checked}
                                onChange={() =>
                                    isRadio
                                        ? selectValue(characteristic.id, value.id)
                                        : toggleValue(characteristic.id, value.id)
                                }
                            />
                            {renderImage && (
                                <img
                                    className="cfg-toggle-item__image"
                                    src={imgSrc}
                                    alt={value.thumbnailAlt ?? label}
                                    loading="lazy"
                                />
                            )}
                            <span className="cfg-toggle-item__text">
                                <span className="cfg-toggle-item__label-text">{label}</span>
                                {info && (
                                    <HtmlContent
                                        tag="span"
                                        className="cfg-toggle-item__info"
                                        html={info}
                                    />
                                )}
                                {/* Legacy renders additional_info right below
                                    the info text (handlebars.js radiobutton /
                                    checkbox, `.infotext-additional`). */}
                                {additionalInfo && (
                                    <HtmlContent
                                        tag="span"
                                        className="cfg-toggle-item__info cfg-toggle-item__additional-info"
                                        html={additionalInfo}
                                    />
                                )}
                            </span>
                            {priceSuffix && (
                                <span className="cfg-toggle-item__price">{priceSuffix}</span>
                            )}
                        </label>
                        {/* Checkbox: one quantity per checked Value, outside
                            the label so typing doesn't toggle the box. */}
                        {!isRadio && qtyMode && checked ? (
                            <QtyInput
                                mode={qtyMode}
                                min={characteristic.minQty}
                                max={characteristic.maxQty}
                                value={qtys?.[value.id]}
                                onChange={(qty) => setPickQty(characteristic.id, qty, value.id)}
                                label={label}
                            />
                        ) : null}
                    </li>
                );
            })}
        </ul>
    );

    // Radio buttons: one quantity for the pick, in front of the list.
    if (!isRadio || !qtyMode) return list;
    return (
        <div className="cfg-qty-block">
            <QtyInput
                mode={qtyMode}
                min={characteristic.minQty}
                max={characteristic.maxQty}
                value={selection?.kind === 'single' ? selection.qty : undefined}
                onChange={(qty) => setPickQty(characteristic.id, qty)}
                label={charLabel}
            />
            {list}
        </div>
    );
}
